import {
  Field,
  Hint,
  Label,
  ReskinFieldRow,
  Textarea,
  useHack,
  useReskin,
} from 'quickstart/blocks/admin/base'
import {type ReactNode, Suspense, lazy, useId} from 'react'

export interface MarkdownEditorProps {
  name: string
  label?: ReactNode
  hint?: ReactNode
  reset?: boolean
}

const Lexical = lazy(async () => {
  // https://github.com/facebook/lexical/issues/6575
  // @ts-expect-error
  globalThis.Prism = await import('prismjs')
  return import('./lexical.tsx').then(d => ({default: d.Lexical}))
})

export const MarkdownEditor = ({reset, ...props}: MarkdownEditorProps) => {
  const id = useId()
  const lexical = useHack('lexical')
  const reskin = useReskin()

  if (!lexical) {
    return <Textarea {...props} />
  }

  const {hint, label, ...rest} = props

  const editor = (fieldId: string) => (
    <Field id={fieldId} {...rest}>
      {({input}) => (
        <Suspense fallback={null}>
          <Lexical
            initialValue={input.value}
            onChange={input.onChange}
            reset={reset}
          />
        </Suspense>
      )}
    </Field>
  )

  // The only control that doesn't reach `adminField`: the hack fork above sits
  // between it and its call site, so the row has to be built here instead.
  // Without this its label and hint would be the one pair in the surface
  // outside a label row — hint under the control, which is where every other
  // field stopped putting it in task 2.5 (design.md — Decision 8).
  if (reskin) {
    return <ReskinFieldRow control={editor} hint={hint} label={label} />
  }

  return (
    <div>
      {!!label && (
        <div>
          <Label htmlFor={id}>{label}</Label>
        </div>
      )}
      <div>{editor(id)}</div>
      {!!hint && <Hint>{hint}</Hint>}
    </div>
  )
}
